import { useEffect, useMemo, useState } from "react";
import mockViews from "../mocks/mockViews";
import { viewsRepositoryMock } from "../services/viewsStorage.mock";

function makeViewId() {
  return `view-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeView(view, index = 0) {
  const source = view || {};
  return {
    id: source.id || makeViewId(),
    name: source.name || `Visão ${index + 1}`,
    isSystem: !!source.isSystem,
    config: {
      filters: { ...(source.config?.filters || {}) },
      groupBy: source.config?.groupBy || "machine",
      visibleColumns: Array.isArray(source.config?.visibleColumns)
        ? [...source.config.visibleColumns]
        : ["label", "plan", "simulated", "realized", "attendancePct", "gap", "status"],
    },
  };
}

export default function useSavedViews() {
  const [views, setViews] = useState(() => {
    const storage = viewsRepositoryMock.loadState();
    const stored = Array.isArray(storage?.views) ? storage.views : null;
    const source = stored && stored.length ? stored : mockViews;
    return source.map(normalizeView);
  });

  const [activeViewId, setActiveViewId] = useState(() => {
    const storage = viewsRepositoryMock.loadState();
    const storedId = storage?.activeViewId || null;
    const source = Array.isArray(storage?.views) ? storage.views : mockViews;
    const exists = source.some((view) => view?.id === storedId);
    return exists ? storedId : (mockViews[0]?.id || null);
  });

  useEffect(() => {
    viewsRepositoryMock.saveState({ views, activeViewId });
  }, [views, activeViewId]);

  useEffect(() => {
    if (!views.length) return;
    if (!views.some((view) => view.id === activeViewId)) {
      setActiveViewId(views[0].id);
    }
  }, [views, activeViewId]);

  const activeView = useMemo(
    () => views.find((view) => view.id === activeViewId) || null,
    [views, activeViewId]
  );

  function createView(name, config) {
    const next = normalizeView({
      id: makeViewId(),
      name,
      isSystem: false,
      config,
    });
    setViews((prev) => [next, ...prev]);
    setActiveViewId(next.id);
    return next;
  }

  function updateView(id, configPatch, nextName) {
    let updated = null;
    setViews((prev) =>
      prev.map((view) => {
        if (view.id !== id) return view;
        updated = normalizeView({
          ...view,
          name: (typeof nextName === "string" ? nextName : view.name) || view.name,
          config: {
            ...view.config,
            ...(configPatch || {}),
            filters: {
              ...(view.config?.filters || {}),
              ...(configPatch?.filters || {}),
            },
            visibleColumns: Array.isArray(configPatch?.visibleColumns)
              ? [...configPatch.visibleColumns]
              : [...(view.config?.visibleColumns || [])],
          },
        });
        return updated;
      })
    );
    return updated;
  }

  function deleteView(id) {
    if (views.length <= 1) {
      return { ok: false, reason: "Mantenha pelo menos 1 visão." };
    }
    const exists = views.some((view) => view.id === id);
    if (!exists) return { ok: false, reason: "Visão não encontrada." };

    setViews((prev) => prev.filter((view) => view.id !== id));
    if (activeViewId === id) {
      const next = views.find((view) => view.id !== id);
      setActiveViewId(next?.id || null);
    }
    return { ok: true };
  }

  return {
    views,
    activeViewId,
    setActiveViewId,
    activeView,
    createView,
    updateView,
    deleteView,
  };
}
